/**
 * Videodan kare okuma yardımcıları.
 * Kareler ana oynatıcıdan değil, `crossOrigin="anonymous"` ile ayrıca yüklenen gizli bir
 * video elemanından okunur: canvas'ın "tainted" olmaması için CORS gerekir, ana oynatıcıya
 * crossOrigin verilirse CORS'suz bir ortamda video hiç oynamaz.
 */

const LOAD_TIMEOUT_MS = 15000;

export class FrameAccessError extends Error {
  constructor() {
    super("frame_access_denied");
    this.name = "FrameAccessError";
  }
}

const loadFrameSource = (url: string) =>
  new Promise<HTMLVideoElement>((resolve, reject) => {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";

    const timer = window.setTimeout(() => {
      cleanup();
      reject(new FrameAccessError());
    }, LOAD_TIMEOUT_MS);
    const cleanup = () => {
      window.clearTimeout(timer);
      video.onloadeddata = null;
      video.onerror = null;
    };

    video.onloadeddata = () => {
      cleanup();
      resolve(video);
    };
    video.onerror = () => {
      cleanup();
      reject(new FrameAccessError());
    };
    video.src = url;
  });

const releaseFrameSource = (video: HTMLVideoElement) => {
  video.removeAttribute("src");
  video.load();
};

const seekTo = (video: HTMLVideoElement, time: number) =>
  new Promise<void>((resolve) => {
    const target = Math.min(Math.max(time, 0), Math.max(video.duration - 0.05, 0));
    if (Math.abs(video.currentTime - target) < 0.01 && video.readyState >= 2) {
      resolve();
      return;
    }
    video.addEventListener("seeked", () => resolve(), { once: true });
    video.currentTime = target;
  });

const drawFrame = (video: HTMLVideoElement, height?: number) => {
  const ratio = video.videoWidth / video.videoHeight || 16 / 9;
  const canvas = document.createElement("canvas");
  canvas.height = height ?? video.videoHeight;
  canvas.width = Math.round(canvas.height * ratio);
  const context = canvas.getContext("2d");
  if (!context) throw new FrameAccessError();
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
};

export interface VideoFrame {
  time: number;
  src: string;
}

/** Eşit aralıklı küçük kareler üretir; CORS yoksa FrameAccessError fırlatır. */
export const extractVideoFrames = async (
  url: string,
  times: number[],
  height: number,
  signal?: { cancelled: boolean }
): Promise<VideoFrame[]> => {
  const video = await loadFrameSource(url);
  try {
    const frames: VideoFrame[] = [];
    for (const time of times) {
      if (signal?.cancelled) break;
      await seekTo(video, time);
      try {
        frames.push({ time, src: drawFrame(video, height).toDataURL("image/jpeg", 0.72) });
      } catch {
        throw new FrameAccessError();
      }
    }
    return frames;
  } finally {
    releaseFrameSource(video);
  }
};

/** Verilen andaki kareyi tam çözünürlükte PNG dosyası olarak döndürür. */
export const captureVideoFrame = async (url: string, time: number, fileName: string) => {
  const video = await loadFrameSource(url);
  try {
    await seekTo(video, time);
    const canvas = drawFrame(video);
    const blob = await new Promise<Blob | null>((resolve) => {
      try {
        canvas.toBlob(resolve, "image/png");
      } catch {
        resolve(null);
      }
    });
    if (!blob) throw new FrameAccessError();
    return new File([blob], fileName, { type: "image/png" });
  } finally {
    releaseFrameSource(video);
  }
};
